/**
 * 뉴스 패널 - 종목별 뉴스 목록 + 감정 분석
 * 네이버 금융 등에서 수집한 뉴스를 표시하고 AI 감정 분석 결과를 보여줍니다.
 */

// 감성에 따른 스타일 (한국 증권 표준: 긍정=상승=빨강, 부정=하락=파랑)
const sentimentStyles = {
  positive: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    text: 'text-[var(--stock-up)]',
    badge: 'bg-red-500/10 text-[var(--stock-up)]',
    icon: '📈',
  },
  negative: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-[var(--stock-down)]',
    badge: 'bg-blue-500/10 text-[var(--stock-down)]',
    icon: '📉',
  },
  neutral: {
    bg: 'bg-[var(--card-bg-hover)]',
    border: 'border-[var(--border-color)]',
    text: 'text-[var(--foreground-muted)]',
    badge: 'bg-[var(--card-bg-hover)] text-[var(--foreground-muted)]',
    icon: '📊',
  },
};

// 날짜 포맷팅
function formatDate(dateStr) {
  try {
    const date = new Date(dateStr.replace(' ', 'T'));
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60));
      return `${minutes}분 전`;
    }
    if (hours < 24) {
      return `${hours}시간 전`;
    }
    const days = Math.floor(hours / 24);
    if (days < 7) {
      return `${days}일 전`;
    }
    return dateStr.split(' ')[0];
  } catch {
    return dateStr;
  }
}

// 뉴스 아이템 컴포넌트
function NewsItemCard({ item }) {
  const style = sentimentStyles[item.sentiment];

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-start gap-3 p-4 rounded-lg border no-underline text-inherit cursor-pointer transition-all duration-200 hover:opacity-80 hover:translate-x-0.5 ${style.bg} ${style.border}`}
    >
      <span className="text-2xl">{style.icon}</span>
      <div className="flex-1 min-w-0">
        <h4 className="text-[var(--foreground)] font-medium text-[0.938rem] m-0 mb-1 line-clamp-2">{item.title}</h4>
        <div className="flex items-center gap-2 text-xs text-[var(--foreground-muted)] flex-wrap">
          <span className={`py-0.5 px-2 rounded ${style.badge}`}>
            {item.sentiment === 'positive' ? '긍정' : item.sentiment === 'negative' ? '부정' : '중립'}
          </span>
          <span>{item.source}</span>
          <span className="opacity-50">•</span>
          <span>{formatDate(item.date)}</span>
        </div>
      </div>
    </a>
  );
}

// 감성 점수 게이지
function SentimentGauge({ score }) {
  // score: -1 ~ 1을 0 ~ 100으로 변환
  const percentage = ((score + 1) / 2) * 100;

  // 한국 증권 표준: 긍정(상승)=빨강, 부정(하락)=파랑
  let colorClass = 'neutral';
  let label = '중립적';

  if (score > 0.3) {
    colorClass = 'positive';
    label = '긍정적';
  } else if (score < -0.3) {
    colorClass = 'negative';
    label = '부정적';
  } else if (score > 0) {
    colorClass = 'positive';
    label = '약간 긍정';
  } else if (score < 0) {
    colorClass = 'negative';
    label = '약간 부정';
  }

  const textColorClass = colorClass === 'positive'
    ? 'text-[var(--stock-up)]'
    : colorClass === 'negative'
      ? 'text-[var(--stock-down)]'
      : 'text-[var(--foreground-muted)]';

  const fillColorClass = colorClass === 'positive'
    ? 'bg-[var(--stock-up)]'
    : colorClass === 'negative'
      ? 'bg-[var(--stock-down)]'
      : 'bg-[var(--foreground-muted)]';

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-[var(--foreground-muted)]">뉴스 감성 분석</span>
        <span className={`text-sm font-medium ${textColorClass}`}>
          {label} ({score > 0 ? '+' : ''}{(score * 100).toFixed(0)}%)
        </span>
      </div>
      <div className="h-3 bg-[var(--background-secondary)] rounded-full overflow-hidden relative">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[var(--border-color)] z-10"></div>
        <div
          className={`absolute top-0 bottom-0 transition-all duration-500 ${fillColorClass}`}
          style={{
            left: score < 0 ? `${percentage}%` : '50%',
            width: `${Math.abs(score) * 50}%`,
          }}
        />
      </div>
      <div className="flex justify-between mt-1 text-xs text-[var(--foreground-muted)] opacity-60">
        <span>부정</span>
        <span>중립</span>
        <span>긍정</span>
      </div>
    </div>
  );
}

export default function NewsPanel({ news }) {
  if (!news) {
    return (
      <div className="flex flex-col items-center justify-center p-12 gap-4">
        <div className="w-12 h-12 border-[3px] border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
        <p>뉴스를 불러오는 중...</p>
      </div>
    );
  }

  const { analysis } = news;

  return (
    <div className="flex flex-col gap-6">
      {/* 뉴스 분석 요약 */}
      <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl p-6">
        <h3 className="text-lg font-bold m-0 mb-4">뉴스 동향 분석</h3>

        {/* 감성 게이지 */}
        <SentimentGauge score={analysis.sentiment_score} />

        {/* 통계 (한국 증권 표준: 긍정=빨강, 부정=파랑) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-2 mt-4">
          <div className="text-center rounded-lg p-3 sm:p-2 bg-red-500/10">
            <div className="text-2xl sm:text-xl font-bold text-[var(--stock-up)]">{analysis.positive_count}</div>
            <div className="text-xs text-[var(--foreground-muted)] mt-1">긍정 뉴스</div>
          </div>
          <div className="text-center rounded-lg p-3 sm:p-2 bg-[var(--card-bg-hover)]">
            <div className="text-2xl sm:text-xl font-bold text-[var(--foreground-muted)]">{analysis.neutral_count}</div>
            <div className="text-xs text-[var(--foreground-muted)] mt-1">중립 뉴스</div>
          </div>
          <div className="text-center rounded-lg p-3 sm:p-2 bg-blue-500/10">
            <div className="text-2xl sm:text-xl font-bold text-[var(--stock-down)]">{analysis.negative_count}</div>
            <div className="text-xs text-[var(--foreground-muted)] mt-1">부정 뉴스</div>
          </div>
        </div>
      </div>

      {/* 뉴스 목록 */}
      <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl p-6">
        <h3 className="text-lg font-bold m-0 mb-4">
          최근 뉴스
          <span className="text-sm font-normal text-[var(--foreground-muted)] ml-2">({analysis.total}건)</span>
        </h3>

        {news.news && news.news.length > 0 ? (
          <div className="flex flex-col gap-3">
            {news.news.map((item, idx) => (
              <NewsItemCard key={idx} item={item} />
            ))}
          </div>
        ) : (
          <div className="text-center p-8 text-[var(--foreground-muted)]">
            최근 뉴스가 없습니다
          </div>
        )}
      </div>

      {/* 출처 안내 */}
      <div className="text-xs text-[var(--foreground-muted)] opacity-60 text-right">
        출처: 네이버 금융
      </div>
    </div>
  );
}
